import React, { createContext, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/browser";
import { IOrganization, IUser, IUserState, ServiceStatusEnum, UserService } from "@imaginelearning/mps-api-service";
import { selectGradeTheme, UxAvatarEnum, UxBackgroundEnum } from "@imaginelearning/mps-ux-component-library";
import { useFeatureFlag } from "@imaginelearning/shared-components/useFeatureFlag";
import { useTranslation } from "react-i18next";
import useSessionStorage from "hooks/useSessionStorage";
import defaultConfig from "../config";
import useTheme from "../hooks/usetheme";
import usePendo from "../hooks/usePendo";

const defaultUserState: IUserState<IUser> = {
  status: ServiceStatusEnum.LOADING,
  response: {
    id: "",
    userId: 0,
    firstname: "",
    middlename: "",
    lastname: "",
    givenname: "",
    tenantid: "",
    email: "",
    avatar: "",
    background: "",
    username: "",
    fullname: "",
    role: "",
    surname: "",
    enabled: "",
    gradeLevel: 0,
    textToSpeech: [],
    languageTranslation: [],
    spanishLanguage: [],
    products: [],
    organization: {
      orgGuid: "",
      orgId: 0,
      parentId: "",
      name: "",
      orgType: "",
      active: false,
      timezone: "",
      academicSession: {
        academicSessionId: "",
        schoolYear: 0,
        startDate: "",
        endDate: ""
      },
      state: "",
      licensedProducts: []
    },
    selectedAvatar: undefined,
    selectedBackground: undefined,
    myPathAssessment: []
  }
};
const defaultDispatchString: React.Dispatch<React.SetStateAction<string>> = (val) => val;
const defaultState = {
  userState: defaultUserState,
  getUser: defaultDispatchString,
  userId: "",
  language: "en",
  setLanguage: defaultDispatchString,
  subject: "",
  setSubject: defaultDispatchString,
  landingSubject: "",
  setLandingSubject: defaultDispatchString,
  isLicensed: false,
  user: {} as IUser,
  organization: {} as IOrganization,
  readyForFlagChecks: false
};
export const UserContext = createContext(defaultState);

export function UserProvider(props: any) {
  const [userState, setUserState] = useState<IUserState<IUser>>(defaultUserState);
  const [userId, setUserId] = useState<string>("");
  const config = defaultConfig();
  const defaultAvatar: UxAvatarEnum = config.defaults.avatar;
  const defaultBackground: UxBackgroundEnum = config.defaults.background;
  const [forceRefetch, getUser] = useState("");

  const { addProperties } = useFeatureFlag();

  const [token] = useSessionStorage("imlpToken", "");

  // Consolidate Language Translation, Text to Speech, and Spanish Language into one location
  const [user, setUser] = useState<IUser>();
  const [activeLanguage, setActiveLanguage] = useState<string>("en");
  const [subject, setSubject] = useState("");
  const [landingSubject, setLandingSubject] = useState("");

  const { i18n } = useTranslation();
  const { setTheme } = useTheme();
  const { initialize: pendoInitialize } = usePendo();
  const [readyForFlagChecks, setReadyForFlagChecks] = useState(false);

  const loadConfig = (result: any) => {
    const user: IUser = result.response as IUser;
    const selectedAvatarText = (name: string) => (name === "EDGE" ? "CAT" : name);

    const selectedAvatar = !user.avatar
      ? defaultAvatar
      : (UxAvatarEnum as any)[selectedAvatarText(user.avatar.toUpperCase())];
    const selectedBackground = !user.background
      ? defaultBackground
      : (UxBackgroundEnum as any)[user.background.toUpperCase()];
    return {
      ...result,
      response: {
        ...user,
        selectedAvatar: selectedAvatar || defaultAvatar,
        selectedBackground: selectedBackground || defaultBackground,
        schoolYear: user.organization?.academicSession?.schoolYear || new Date().getFullYear(),
        orgTimeZone: user.organization?.timezone
      }
    };
  };

  const fetchUserData = async () => {
    if (!token) {
      return;
    }
    const payload = JSON.parse(atob(token.split(".")[1]));

    const userIdFromToken = payload.tenant_user_id;
    let result = await UserService.getUserById(userIdFromToken);

    if (result.status === ServiceStatusEnum.LOADED) {
      result = loadConfig(result);
    }
    setUserState(result);
    setUser(result.response as IUser);

    const productsLicensed = result.response?.products || [];

    await pendoInitialize(userIdFromToken, productsLicensed);

    // Sentry userguid
    Sentry.setUser({ id: userIdFromToken });
  };

  const setSubjectLanguage = () => {
    if (user?.spanishLanguage?.includes(subject.toLowerCase())) {
      setActiveLanguage("es");
      document.documentElement.lang = "es";
      i18n?.changeLanguage("es");
    } else {
      setActiveLanguage("en");
      document.documentElement.lang = "en";
      i18n?.changeLanguage("en");
    }
  };

  useEffect(() => {
    setSubjectLanguage();
  }, [user, subject]);

  const setLanguage = (language: string) => {
    setActiveLanguage(language);
  };

  useEffect(() => {
    if (!addProperties || !user?.organization?.orgGuid) {
      return;
    }
    const orgGuid = user?.organization?.orgGuid;
    const parentOrgGuid = user?.organization?.parentOrganization?.orgGuid;
    const FeatureFlagProperties: { [key: string]: string } = {};
    if (orgGuid) {
      FeatureFlagProperties.school_id = orgGuid;
      FeatureFlagProperties.environment = currentEnvironment;
      if (parentOrgGuid && parentOrgGuid !== "null") {
        FeatureFlagProperties.district_id = parentOrgGuid;
      }
      addProperties(FeatureFlagProperties);
      setReadyForFlagChecks(true);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setTheme(selectGradeTheme(user.gradeLevel));
    }
  }, [user]);

  const getTokenPayload = () => {
    const token: any = sessionStorage.getItem("imlpToken");
    if (token) {
      return JSON.parse(atob(token.split(".")[1]));
    }
    return "";
  };

  const currentEnvironment =
    window.location.host === "student.mp.edgenuity.com" || window.location.host === "student-mp.imaginelearning.com"
      ? "prod"
      : "non-prod";

  useEffect(() => {
    const load = async () => {
      const payload = getTokenPayload();

      let tempAvatar = "";
      let tempBackground = "";
      const userInfo = localStorage.getItem("userInfo");
      let userData: any = {};
      if (userInfo) {
        userData = JSON.parse(userInfo);
      }
      const userIdFromToken = payload.tenant_user_id;
      setUserId(userIdFromToken);
      if (userData[userIdFromToken]) {
        tempAvatar = userData[userIdFromToken].selectedAvatar;
        tempBackground = userData[userIdFromToken].selectedBackground;
        setUserState({
          ...userState,
          response: {
            ...userState.response,
            selectedAvatar: tempAvatar,
            selectedBackground: tempBackground
          }
        });
      }
    };
    load();
  }, []);

  useEffect(() => {
    if ((userState.response as IUser)?.id) {
      const userInfo = localStorage.getItem("userInfo");
      let userData: any = {};
      if (userInfo) {
        userData = JSON.parse(userInfo);
      }
      userData[(userState.response as IUser).id] = {
        selectedBackground: (userState.response as IUser).selectedBackground,
        selectedAvatar: (userState.response as IUser).selectedAvatar
      };
      if (!userData) {
        userData = {};
      }
      localStorage.setItem("userInfo", JSON.stringify(userData));
    }
  }, [userState, userId]);

  useEffect(() => {
    fetchUserData();
  }, [forceRefetch, defaultAvatar, defaultBackground]);

  const value = useMemo(
    () => ({
      user,
      userState,
      getUser,
      userId,
      language: activeLanguage,
      setLanguage,
      subject,
      setSubject,
      landingSubject,
      setLandingSubject,
      readyForFlagChecks
    }),
    [
      user,
      userState,
      getUser,
      userId,
      activeLanguage,
      setLanguage,
      subject,
      setSubject,
      landingSubject,
      setLandingSubject,
      readyForFlagChecks
    ]
  );

  return <UserContext.Provider key="user.provider" value={value} {...props} />;
}
